import { Modal, Select, TextInput } from "risa-oasis-ui_v2";
import { JCodeDetails } from "../../../data-model/medicalPaOrdersModel";
import { PortalConfig } from "../../../data-model/portalConfigForDraftSubmission";
import { ModalId } from "../../../enums/modalId";
import DragDropUpload from "../../dragDropUpload/dragDropUpload";
import InlineLoader from "./components/inlineLoader";
import MultiSelectJCode from "./components/multiSelectJCode";
import {
  DraftSubmissionFormData,
  useDraftSubmissionFormDataWithErrorStates,
} from "./hooks/useDraftSubmissionFormDataWithErrorStates";
import { useGeneratedPathForFileUpload } from "./hooks/useGeneratedPathForFileUpload";
import { useJCodeOptions } from "./hooks/useJCodeOptions";
import { usePortalConfigForDraftSubmission } from "./hooks/usePortalConfigForDraftSubmission";
import { handleDocUpload } from "./utils/handleDocUpload";

interface DraftSubmissionModalProps {
  jCodes: JCodeDetails[];
  onClose: () => void;
  onSave: (
    formData: DraftSubmissionFormData,
    jCodes: JCodeDetails[],
    portalConfigData: PortalConfig[],
  ) => void;
  onCancel: () => void;
  orderId: string;
  isSaveDisabled: boolean;
  selectedJCodeId: string;
}

const DraftSubmissionModal = (props: DraftSubmissionModalProps) => {
  const { jCodes, onClose, onCancel, orderId, isSaveDisabled } = props;
  const { formData, setFormData, errorStates, checkIfFormDataIsInvalid } =
    useDraftSubmissionFormDataWithErrorStates(props.selectedJCodeId);
  const jCodeOptions = useJCodeOptions(jCodes);
  const { portalConfigData, getPortalConfigOptions } =
    usePortalConfigForDraftSubmission();
  const generatedPath = useGeneratedPathForFileUpload(orderId);

  const handleSave = () => {
    props.onSave(formData, jCodes, portalConfigData.portalConfig ?? []);
  };

  return (
    <Modal
      dialogId={ModalId.DRAFT_SUBMISSION_MODAL}
      title="Auth Review Information"
      onClose={onClose}
      onCancel={onCancel}
      onSave={handleSave}
      saveButtonText={isSaveDisabled ? "Saving..." : "Save"}
      cancelText="Cancel"
      heightPercentage={70}
      disableSave={checkIfFormDataIsInvalid() || isSaveDisabled}
    >
      <div className="draft-submission-modal--container flex flex-col gap-4">
        {/* Auth ID Text Box */}
        <TextInput
          id={"auth-id"}
          label={"Auth ID"}
          placeholder={"Enter Auth ID"}
          required
          onChange={(e) =>
            setFormData((prevFormData) => ({
              ...prevFormData,
              authId: e.value,
            }))
          }
        />

        {/* Portal Name Dropdown */}
        {portalConfigData.loading ? (
          <InlineLoader />
        ) : (
          <>
            <Select
              id={"portal-name"}
              label={"Portal Name"}
              placeholder={"Select Portal Name"}
              options={getPortalConfigOptions}
              onOptionChange={(e) =>
                setFormData((prevFormData) => ({
                  ...prevFormData,
                  portalName: e.value,
                }))
              }
              required
            />
          </>
        )}

        {/* Multi Select Drugs / JCodes */}
        <MultiSelectJCode
          formData={formData}
          setFormData={setFormData}
          errorStates={errorStates}
          jCodeOptions={jCodeOptions}
          selectedJCodeId={props.selectedJCodeId}
        />

        {/* Doc Upload */}
        <div className="doc-upload-container flex flex-col gap-1">
          <DragDropUpload
            id={"draft-submission-doc-upload"}
            subText={"Please upload a .jpg, .jpeg, or .png file"}
            multipleFilesAllowed={false}
            allowedFileTypes={[".jpg", ".jpeg", ".png"]}
            maxFileSize={10 * 1024 * 1024}
            filePath={generatedPath}
            fileListLength={(length) =>
              handleDocUpload(length, orderId, setFormData, generatedPath)
            }
          />
          {errorStates.filePath && (
            <div className="doc-upload-container--error text-tiny text-tertiaryRed-form_error">
              Please upload a file
            </div>
          )}
        </div>

        {/* Comments Area */}
        <div className="draft-submission-modal--comments">
          <div className="draft-submission-modal--comments-label mb-1 text-tiny">
            Comments
          </div>
          <div className="draft-submission-modal--comments-textarea">
            <textarea
              className="draft-submission-modal--comments-textarea w-full rounded border border-primaryGray-14 px-3 py-2 text-tiny outline-none"
              rows={5}
              value={formData.comments}
              onChange={(e) =>
                setFormData((prevFormData) => ({
                  ...prevFormData,
                  comments: e.target.value,
                }))
              }
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DraftSubmissionModal;
